import { useTheme } from '../hooks/useTheme';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCartStore } from '../store/cart.store';
import { ProductsService } from '../services/products/products.service';
import type { ProductSearchResult } from '../services/products/products.service';
import { getFirstProductImage } from '../utils/product-images';
import { openWhatsAppProduct } from '../utils/whatsapp';
import { RegistroModal } from '../components/modals/RegistroModal';
import { ProductCard } from '../components/products/ProductCard';
import { 
  Search, 
  Filter, 
  ShoppingCart, 
  Heart, 
  Check,
  Pill,
  Baby,
  Activity,
  Sparkles,
  Shield,
  Loader2,
  Package,
  Droplets
} from 'lucide-react';

export const ProductosPage = () => {
  const { theme } = useTheme();
  const { addItem } = useCartStore();
  const [searchParams] = useSearchParams();
  const [categoriaActiva, setCategoriaActiva] = useState('todos');
  const [precioMaximo, setPrecioMaximo] = useState(500000);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [addedItems, setAddedItems] = useState<string[]>([]);
  const [productos, setProductos] = useState<ProductSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [showRegistroModal, setShowRegistroModal] = useState(false);
  const [presSeleccionada, setPresSeleccionada] = useState<Record<string, string>>({});
  const [mostrados, setMostrados] = useState(10);
  const [ordenSeleccionado, setOrdenSeleccionado] = useState('Relevancia');
  const [dropdownOrdenAbierto, setDropdownOrdenAbierto] = useState(false);

  const categorias = [
    { id: 'todos', nombre: 'Todos', icono: null },
    { id: 'medicamentos', nombre: 'Medicamentos', icono: <Pill className="w-4 h-4" /> },
    { id: 'cuidado-personal', nombre: 'Cuidado Personal', icono: <Sparkles className="w-4 h-4" /> },
    { id: 'higiene-femenina', nombre: 'Higiene Femenina', icono: <Droplets className="w-4 h-4" /> },
    { id: 'salud-sexual', nombre: 'Salud Sexual', icono: <Heart className="w-4 h-4" /> },
    { id: 'vitaminas-y-suplementos', nombre: 'Vitaminas y Suplementos', icono: <Activity className="w-4 h-4" /> },
    { id: 'bebes', nombre: 'Bebés', icono: <Baby className="w-4 h-4" /> },
    { id: 'equipos-medicos', nombre: 'Equipos Médicos', icono: <Shield className="w-4 h-4" /> },
  ];

  // Leer parámetro de búsqueda de la URL
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setBusqueda(decodeURIComponent(searchParam));
    }
  }, [searchParams]);

  // Cargar productos desde el backend
  useEffect(() => {
    loadProductos();
  }, []);

  // Resetear cantidad mostrada cuando cambien filtros
  useEffect(() => {
    setMostrados(10);
  }, [busqueda, categoriaActiva, precioMaximo]);

  const loadProductos = async () => {
    setLoading(true);
    try {
      const response = await ProductsService.getPublic(999);
      if (response.success) {
        setProductos(response.data);
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Iconos por categoría
  const getIconoCategoria = (categoria: string) => {
    const iconos: Record<string, string> = {
      'medicamentos': '💊',
      'cuidado-personal': '🧴',
      'higiene-femenina': '🌸',
      'salud-sexual': '💕',
      'vitaminas-y-suplementos': '🍊',
      'bebes': '👶',
      'equipos-medicos': '🏥',
    };
    return iconos[categoria] || '📦';
  };

  const productosFiltrados = productos.filter(p => {
    const matchCategoria = categoriaActiva === 'todos' || p.categoria === categoriaActiva;
    const matchPrecio = (p.presentaciones?.[0]?.precio || 0) <= precioMaximo;
    const matchBusqueda = busqueda === '' || p.nombre.toLowerCase().includes(busqueda.toLowerCase());
    return matchCategoria && matchPrecio && matchBusqueda;
  });

  const getPrecioSeleccionado = (producto: ProductSearchResult) => {
    const presId = presSeleccionada[producto.id];
    const pres = presId
      ? producto.presentaciones?.find(p => p.id === presId)
      : producto.presentaciones?.[0];
    return pres;
  };

  const handleAddToCart = (producto: ProductSearchResult) => {
    const pres = getPrecioSeleccionado(producto);
    if (!pres) return;
    addItem({
      id: producto.id,
      nombre: producto.nombre,
      precio: pres.precio || 0,
      precioAntes: pres.precioAntes,
      imagen: getFirstProductImage(producto.imagenes) || getIconoCategoria(producto.categoria),
      receta: producto.requiereReceta,
      categoria: producto.categoria,
      presentacionId: pres.id,
      presentacionNombre: pres.nombre,
    }, true);

    // Mostrar feedback visual temporal
    setAddedItems(prev => [...prev, producto.id]);
    setTimeout(() => {
      setAddedItems(prev => prev.filter(id => id !== producto.id));
    }, 1500);
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${
      theme === 'dark'
        ? 'bg-gray-900'
        : 'bg-gray-50'
    }`}>

      {/* Header con banner CTA integrado */}
      <section className="w-full mx-auto px-2 sm:px-3 lg:px-4 pt-4">
        <div className={`rounded-xl p-4 sm:p-5 mb-6 relative overflow-hidden ${
          theme === 'dark' 
            ? 'bg-gradient-to-r from-[#4CAF50]/30 to-[#2196F3]/30 border border-[#4CAF50]/20' 
            : 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3]'
        }`}>
          <div className="relative">
            {/* Título y subtítulo */}
            <div className="mb-3">
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Nuestros Productos
              </h1>
              <p className="text-white/80 text-sm">
                Todo para tu salud y bienestar · <span className="text-yellow-300 font-semibold">¡Precios especiales! Hasta 25% OFF</span>
              </p>
            </div>

            {/* Beneficios y acciones */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p className="text-white/90 text-sm">
                Envío gratis +$50k · Tracking · Lun-Dom: 8:00 AM - 9:00 PM
              </p>
              
              <div className="flex gap-3 w-full sm:w-auto">
                <button 
                  onClick={() => setShowRegistroModal(true)}
                  className="flex-1 sm:flex-none px-5 py-2.5 bg-white text-[#0d5c3b] font-bold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm whitespace-nowrap"
                >
                  Registrarme
                </button>
                <a 
                  href="/login"
                  className="flex-1 sm:flex-none px-5 py-2.5 border-2 border-white/60 text-white font-semibold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:bg-white/10 transition-all text-sm whitespace-nowrap"
                >
                  Ingresar
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Búsqueda - visible solo en mobile */}
        <div className="relative max-w-xs w-full mb-4 lg:hidden">
          <input
            type="text"
            placeholder="Buscar productos..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className={`w-full pl-4 pr-10 py-2 rounded-lg border transition-all duration-300 ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-[#4CAF50]'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2196F3]'
            }`}
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
      </section>

      <div className="w-full mx-auto px-2 sm:px-3 lg:px-4 pb-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Sidebar - Filtros + Búsqueda (sticky en desktop) */}
          <div className={`lg:w-72 flex-shrink-0 ${mostrarFiltros ? 'block' : 'hidden lg:block'}`}>
            <div className="lg:sticky lg:top-[130px] lg:z-10 lg:max-h-[calc(100vh-150px)] lg:overflow-y-auto">
              <div className={`p-6 rounded-2xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-sm'}`}>
                {/* Búsqueda - visible solo en desktop */}
                <div className="relative w-full mb-6 hidden lg:block">
                  <input
                    type="text"
                    placeholder="Buscar productos..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className={`w-full pl-4 pr-10 py-2 rounded-lg border transition-all duration-300 ${
                      theme === 'dark'
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-[#4CAF50]'
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2196F3]'
                    }`}
                  />
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>

                <div className="flex items-center justify-between mb-6">
                  <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    <Filter className="w-5 h-5 inline mr-2" />
                    Filtros
                  </h3>
                </div>

                {/* Categorías */}
                <div className="mb-6">
                  <h4 className={`font-semibold mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Categorías
                  </h4>
                  <div className="space-y-2">
                    {categorias.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setCategoriaActiva(cat.id)}
                        className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-left transition-colors ${
                          categoriaActiva === cat.id
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {cat.icono && <span>{cat.icono}</span>}
                        <span>{cat.nombre}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Rango de precio */}
                <div>
                  <h4 className={`font-semibold mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Precio Máximo: ${precioMaximo.toLocaleString()}
                  </h4>
                  <input
                    type="range"
                    min="0"
                    max="500000"
                    step="10000"
                    value={precioMaximo}
                    onChange={(e) => setPrecioMaximo(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#4CAF50]"
                  />
                  <div className="flex justify-between text-sm mt-2 text-gray-500">
                    <span>$0</span>
                    <span>$500.000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Productos Grid */}
          <main className="flex-1">
            {/* Mobile filter toggle */}
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="lg:hidden mb-4 w-full py-3 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] text-white rounded-xl font-medium flex items-center justify-center space-x-2"
            >
              <Filter className="w-5 h-5" />
              <span>Mostrar Filtros</span>
            </button>

            {/* Resultados */}
            <div className="flex items-center justify-end mb-6">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Ordenar por:</span>
                <div className="relative" ref={(el) => {
                  if (el) {
                    const handleClickOutside = (e: MouseEvent) => {
                      if (!el.contains(e.target as Node)) setDropdownOrdenAbierto(false);
                    };
                    document.addEventListener('mousedown', handleClickOutside);
                  }
                }}>
                  <button
                    onClick={() => setDropdownOrdenAbierto(!dropdownOrdenAbierto)}
                    className="px-3 py-2 rounded-lg border text-sm bg-white border-gray-200 text-gray-900 flex items-center gap-2 min-w-[180px] justify-between hover:border-emerald-400 transition-colors"
                  >
                    {ordenSeleccionado}
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOrdenAbierto ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {dropdownOrdenAbierto && (
                    <div className="absolute right-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                      {['Relevancia', 'Precio: Menor a Mayor', 'Precio: Mayor a Menor', 'Más Vendidos'].map((opcion) => (
                        <button
                          key={opcion}
                          onClick={() => { setOrdenSeleccionado(opcion); setDropdownOrdenAbierto(false); }}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                            ordenSeleccionado === opcion
                              ? 'bg-emerald-50 text-emerald-700 font-medium'
                              : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }`}
                        >
                          {opcion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4 items-stretch">
              {loading ? (
                <div className="col-span-full flex items-center justify-center py-20">
                  <Loader2 className="w-12 h-12 animate-spin text-[#4CAF50]" />
                </div>
              ) : productosFiltrados.length === 0 ? (
                <div className="col-span-full text-center py-16">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className={`text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    No se encontraron productos
                  </h3>
                  <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Intenta con otros filtros o categorías
                  </p>

                  {/* CTA en empty state */}
                  <div className={`max-w-md mx-auto p-6 rounded-2xl ${
                    theme === 'dark'
                      ? 'bg-emerald-900/20 border border-emerald-500/20'
                      : 'bg-emerald-50 border border-emerald-200'
                  }`}>
                    <div className="text-2xl mb-3">💡</div>
                    <h4 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      ¿Buscas algo específico?
                    </h4>
                    <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      Regístrate gratis y te ayudamos a encontrar exactamente lo que necesitas
                    </p>
                    <button
                      onClick={() => setShowRegistroModal(true)}
                      className="inline-block px-6 py-3 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white font-semibold rounded-xl transition-all duration-300 hover:scale-105"
                    >
                      Crear cuenta gratis
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {productosFiltrados.slice(0, mostrados).map((producto) => (
                    <ProductCard
                      key={producto.id}
                      producto={producto}
                      theme={theme}
                      added={addedItems.includes(producto.id)}
                      onAddToCart={(p, presId, presNombre) => {
                        const pres = p.presentaciones?.find(pr => pr.id === presId);
                        if (!pres) return;
                        addItem({
                          id: p.id,
                          nombre: p.nombre,
                          precio: pres.precio || 0,
                          precioAntes: pres.precioAntes,
                          imagen: getFirstProductImage(p.imagenes) || getIconoCategoria(p.categoria),
                          receta: p.requiereReceta,
                          categoria: p.categoria,
                          presentacionId: pres.id,
                          presentacionNombre: pres.nombre,
                        }, true);
                        setAddedItems(prev => [...prev, p.id]);
                        setTimeout(() => {
                          setAddedItems(prev => prev.filter(id => id !== p.id));
                        }, 1500);
                      }}
                    />
                  ))}
                  {/* Botón Ver Más */}
                  {productosFiltrados.length > mostrados && (
                    <div className="col-span-full flex justify-center mt-6">
                      <button
                        onClick={() => setMostrados(prev => prev + 10)}
                        className="px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                      >
                        Ver más ({productosFiltrados.length - mostrados} restantes)
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>


          </main>
        </div>
      </div>

      {/* Modal de Registro */}
      <RegistroModal 
        isOpen={showRegistroModal}
        onClose={() => setShowRegistroModal(false)}
        onSuccess={() => {
        }}
      />
    </div>
  );
};

export default ProductosPage;
